import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dimensions, Text } from 'react-native'
import { Chip, Icon, Menu, Searchbar } from 'react-native-paper'
import { useTheme } from '../../../../hooks/theme.hook'
import { IconNode } from '../../../../utils/meldd-icons'
import { useNavigation } from '@react-navigation/native'
import { AppNavigation } from '../../../../navigation/types'
import { ActivityIndicator } from 'react-native'
import { useIsMobileDevice } from '../../../../hooks/is-mobile.hook'
import { MdNode } from '../nodes-list/types'

export type NodeSelectorPublicOption = {
  title: string
  onSelect: () => void
}

interface NodeSelectorProps {
  selected?: MdNode
  nodes: MdNode[]
  onSelect: (node: MdNode) => void
  loading?: boolean
  /** When there is no `selected` node, show this label instead of the default “no node” string. */
  emptySelectionLabel?: string
  /** Extra menu row (e.g. public co-rank) to clear node selection; shown when title matches search. */
  publicOption?: NodeSelectorPublicOption
}

export default function NodeSelector(props: NodeSelectorProps) {
  const windowWidth = Dimensions.get('window').width
  const navigation = useNavigation<AppNavigation>()
  const theme = useTheme()
  const { t } = useTranslation('nodes')
  const [searchQuery, setSearchQuery] = useState('')
  const [visible, setVisible] = useState(false)
  const isMobile = useIsMobileDevice()
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const onSelection = useCallback(
    (node: MdNode) => {
      setVisible(false)
      props.onSelect(node)
    },
    [props.onSelect]
  )
  const menuItems = useMemo(
    () =>
      props.nodes
        .filter((node) =>
          node.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .map((node) => (
          <Menu.Item
            key={node.id}
            style={{ width: '100%', maxWidth: '100%' }}
            leadingIcon={IconNode}
            onPress={() => onSelection(node)}
            title={node.name}
          />
        )),
    [onSelection, props.nodes, searchQuery]
  )

  const publicOptionVisible =
    props.publicOption &&
    props.publicOption.title.toLowerCase().includes(searchQuery.toLowerCase())

  // if (isMobile) {
  //   return (
  //     <TouchableIconWithToolTip
  //       mode="text"
  //       icon={<Icon source={IconNode} size={18} color={theme.colors.primary} />}
  //       tooltip={props.selected?.name || ''}
  //       onPress={() => navigation.push('ListNodes')}
  //     />
  //   )
  // }
  return (
    <Menu
      visible={visible}
      anchorPosition={'bottom'}
      style={{
        flex: 1,
        marginTop: theme.spacing(1),
        width: windowWidth - 2 * theme.spacing(4),
      }}
      contentStyle={{
        elevation: 10,
        zIndex: 1001,
      }}
      onDismiss={() => {
        // Only dismiss if search isn't focused
        if (!isSearchFocused) {
          setVisible(false)
        }
      }}
      anchor={
        <Chip
          icon={
            props.loading
              ? () => <ActivityIndicator />
              : () => (
                  <Icon
                    source={IconNode}
                    size={isMobile ? 12 : 18}
                    color={theme.colors.primary}
                  />
                )
          }
          mode="outlined"
          style={{
            width: isMobile ? 120 : undefined,
          }}
          closeIcon={visible ? 'menu-up' : 'menu-down'}
          onPress={() => setVisible((prev) => !prev)}
          onClose={() => setVisible((prev) => !prev)}
        >
          <Text style={{ fontSize: isMobile ? 12 : 18 }}>
            {props.loading
              ? ''
              : props.selected?.name ??
                props.emptySelectionLabel ??
                t('no-node-selected')}
          </Text>
        </Chip>
      }
    >
      <Searchbar
        placeholder={t('search-nodes')}
        onChangeText={setSearchQuery}
        value={searchQuery}
        onBlur={() => setIsSearchFocused(false)}
        onFocus={() => setIsSearchFocused(true)}
      />
      {publicOptionVisible ? (
        <Menu.Item
          key="node-selector-public-option"
          style={{ width: '100%', maxWidth: '100%' }}
          onPress={() => {
            setVisible(false)
            props.publicOption?.onSelect()
          }}
          title={props.publicOption.title}
        />
      ) : null}
      {menuItems}
      <Menu.Item
        key={'all'}
        style={{ width: '100%', maxWidth: '100%' }}
        onPress={() => {
          navigation.navigate('ListNodes')
          setVisible(false)
        }}
        title={t('node-list')}
      />
    </Menu>
  )
}
